/**
 * Lightweight image compression utility (canvas-based, browser-native).
 * Resizes an image File to at most `maxWidth`x`maxHeight` and re-encodes
 * as JPEG with the given quality. Returns a new File object.
 *
 * Skips compression for non-images and very small files.
 */
export async function compressImage(file, opts = {}) {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.85,
    minSizeKb = 200,     // don't bother if file already smaller than this
    forceJpeg = true,    // re-encode PNG/HEIC/etc. to JPEG
    outputName,          // optional custom name; otherwise derives from original
  } = opts;

  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.size <= minSizeKb * 1024 && !forceJpeg) return file;

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("Image load failed"));
      im.src = url;
    });

    let { width, height } = img;
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // White background flatten (for PNG transparency → JPEG)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", quality);
    });
    if (!blob) return file;

    // Use compressed only if actually smaller
    if (blob.size >= file.size && file.type === "image/jpeg") return file;

    const baseName = (outputName || file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file; // graceful fallback
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Returns a human-readable size string from bytes.
 */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
